import { useI18n, type MessageKey } from '../i18n/index.tsx';
import { PlayingCard } from '../components/PlayingCard.tsx';
import type { CardId } from '@rummy/engine';

/**
 * The full rules, at `/rules`.
 *
 * The three-step `RulePrimer` answers "what do I do on my turn"; this answers
 * "how does this game work", which is a different question and the one a
 * newcomer invited to somebody else's table actually has. It is a route rather
 * than a modal so it can be linked to — the most common way a new player
 * arrives is somebody sending them a link, and "read this first" has to be a
 * link too.
 *
 * Every example is drawn with the real card component, so what is described
 * here is literally what the table will look like.
 */

/** A row of example cards, with the wild ones marked as the table marks them. */
function Cards({ ids, wild = [] }: { ids: CardId[]; wild?: CardId[] }) {
  return (
    <div className="rules__cards" dir="ltr">
      {ids.map((id) => (
        <PlayingCard key={id} id={id} wild={wild.includes(id)} feature />
      ))}
    </div>
  );
}

function Example({
  ids,
  wild,
  verdict,
  label,
}: {
  ids: CardId[];
  wild?: CardId[];
  verdict: 'yes' | 'no';
  label: string;
}) {
  return (
    <div className={`rules__example rules__example--${verdict}`}>
      <Cards ids={ids} {...(wild ? { wild } : {})} />
      <p className="rules__verdict">
        <span aria-hidden="true">{verdict === 'yes' ? '✓' : '✗'}</span> {label}
      </p>
    </div>
  );
}

export function RulesPage({ onBack }: { onBack: () => void }) {
  const { t } = useI18n();
  const s = (k: string) => t(`rules.${k}` as MessageKey);

  return (
    <div className="screen rules">
      <header className="rules__head">
        <button type="button" className="btn btn--ghost" onClick={onBack}>
          {s('back')}
        </button>
        <h1>{s('heading')}</h1>
        <p className="rules__lede">{s('lede')}</p>
      </header>

      <section className="rules__section">
        <h2>{s('goal.title')}</h2>
        <p>{s('goal.body')}</p>
      </section>

      <section className="rules__section">
        <h2>{s('table.title')}</h2>
        <p>{s('table.body')}</p>
        <p>{s('table.wild')}</p>
      </section>

      <section className="rules__section">
        <h2>{s('turn.title')}</h2>
        <ol className="rules__steps">
          <li>{s('turn.draw')}</li>
          <li>{s('turn.arrange')}</li>
          <li>{s('turn.discard')}</li>
        </ol>
        <p className="rules__aside">{s('turn.note')}</p>
      </section>

      <section className="rules__section">
        <h2>{s('combos.title')}</h2>

        <h3>{s('pure.title')}</h3>
        <p>{s('pure.body')}</p>
        <Example ids={['5H:0', '6H:0', '7H:0']} verdict="yes" label={s('pure.yes')} />
        <Example ids={['5H:0', '6S:0', '7H:0']} verdict="no" label={s('pure.no')} />
        <Example ids={['KS:0', 'AS:0', '2S:0']} verdict="no" label={s('pure.corner')} />

        <h3>{s('impure.title')}</h3>
        <p>{s('impure.body')}</p>
        <Example
          ids={['5H:0', 'JK:0', '7H:0']}
          wild={['JK:0']}
          verdict="yes"
          label={s('impure.yes')}
        />

        <h3>{s('set.title')}</h3>
        <p>{s('set.body')}</p>
        <Example ids={['9S:0', '9H:0', '9D:0']} verdict="yes" label={s('set.yes')} />
        <Example ids={['9S:0', '9S:1', '9D:0']} verdict="no" label={s('set.no')} />
      </section>

      <section className="rules__section">
        <h2>{s('declare.title')}</h2>
        <p>{s('declare.body')}</p>
        <ul className="rules__rules">
          <li>{s('declare.r1')}</li>
          <li>{s('declare.r2')}</li>
          <li>{s('declare.r3')}</li>
        </ul>
        <p className="rules__warn">{s('declare.warn')}</p>
      </section>

      <section className="rules__section">
        <h2>{s('score.title')}</h2>
        <p>{s('score.body')}</p>
        <table className="rules__table">
          <tbody>
            <tr><th scope="row">{s('score.win')}</th><td>{s('score.win.v')}</td></tr>
            <tr><th scope="row">{s('score.card')}</th><td>{s('score.card.v')}</td></tr>
            <tr><th scope="row">{s('score.wild')}</th><td>{s('score.wild.v')}</td></tr>
            <tr><th scope="row">{s('score.cap')}</th><td>{s('score.cap.v')}</td></tr>
            <tr><th scope="row">{s('score.first')}</th><td>{s('score.first.v')}</td></tr>
            <tr><th scope="row">{s('score.middle')}</th><td>{s('score.middle.v')}</td></tr>
            <tr><th scope="row">{s('score.invalid')}</th><td>{s('score.invalid.v')}</td></tr>
          </tbody>
        </table>
        <p className="rules__aside">{s('score.protect')}</p>
      </section>

      <section className="rules__section">
        <h2>{s('formats.title')}</h2>
        <p>{s('formats.deals')}</p>
        <p>{s('formats.pool')}</p>
      </section>

      <section className="rules__section">
        <h2>{s('tips.title')}</h2>
        <ul className="rules__rules">
          <li>{s('tips.pure')}</li>
          <li>{s('tips.arrange')}</li>
          <li>{s('tips.drop')}</li>
          <li>{s('tips.wild')}</li>
        </ul>
      </section>

      <footer className="rules__foot">
        <button type="button" className="btn btn--primary" onClick={onBack}>
          {s('play')}
        </button>
      </footer>
    </div>
  );
}
